/**
 * Summer bundle upsell.
 *
 * Adds the product being viewed plus any ticked partners in a single
 * `/cart/add.js` call, then announces it with the theme's own
 * `CartLinesUpdateEvent`. That event is what `cart-items-component` and
 * `cart-drawer-component` already listen for on `document`, so the drawer opens
 * and refreshes itself — and because we attach no `sections` to the detail,
 * `cart-items-component` refetches, which keeps the discount UI honest after the
 * mutation. Re-implementing section morphing here would have been a second copy
 * of logic the theme already owns.
 *
 * The saving shown is display only. Nothing in Liquid or the AJAX cart can apply
 * a cart discount; the money comes off via the native automatic discount, which
 * has to be configured to match the block's `discount_percent`.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/** Renders cents using the shop's own money format, so EGP looks like the rest of the storefront. */
function formatMoney(cents, format) {
  const withCommas = (n, decimals) => {
    const fixed = (n / 100).toFixed(decimals);
    const [int, dec] = fixed.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return dec ? `${grouped}.${dec}` : grouped;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount_no_decimals':
      case 'amount_no_decimals_with_comma_separator':
        return withCommas(cents, 0);
      default:
        return withCommas(cents, 2);
    }
  });
}

class FandomBundle extends HTMLElement {
  #current = {};
  #moneyFormat = '{{amount}}';
  #percent = 0;
  #busy = false;

  connectedCallback() {
    this.#percent = Number(this.dataset.percent || 0);
    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';

    const json = this.querySelector('[data-bundle-current]');
    try {
      this.#current = json ? JSON.parse(json.textContent) : {};
    } catch {
      this.#current = {};
    }

    this.addEventListener('change', () => this.#render());
    // The page's variant picker writes to a hidden input's *property*, which a
    // MutationObserver on attributes never sees. Listening on the document for
    // any change or click and re-reading is cheap and cannot miss an update; the
    // value is re-read at submit time regardless, so the add is always correct
    // even if a repaint were somehow skipped.
    this.#docHandler = () => this.#render();
    document.addEventListener('change', this.#docHandler);
    document.addEventListener('click', this.#docHandler);

    this.querySelector('[ref="submit"]')?.addEventListener('click', () => this.#submit());
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#docHandler);
    document.removeEventListener('click', this.#docHandler);
  }

  #docHandler = () => {};

  /** The variant id the page's own product form currently holds. */
  #currentVariantId() {
    const input = document.querySelector('product-form-component input[name="id"]');
    return input instanceof HTMLInputElement ? input.value : null;
  }

  /** Every line the bundle would add: the current product first, then ticked partners. */
  #lines() {
    const lines = [];
    const id = this.#currentVariantId();
    if (id && this.#current[id]) lines.push({ id, price: this.#current[id].price, quantity: 1 });

    for (const row of this.querySelectorAll('.fandom-bundle__item')) {
      const check = row.querySelector('.fandom-bundle__check');
      if (!check?.checked) continue;
      const select = row.querySelector('.fandom-bundle__variant');
      const option = select?.selectedOptions?.[0];
      if (!option) continue;
      lines.push({ id: option.value, price: Number(option.dataset.price || 0), quantity: 1 });
    }
    return lines;
  }

  #render() {
    const id = this.#currentVariantId();
    const currentVariant = id ? this.#current[id] : null;

    const sizeEl = this.querySelector('[ref="currentSize"]');
    if (sizeEl) sizeEl.textContent = currentVariant ? currentVariant.title : '';

    const priceEl = this.querySelector('[ref="currentPrice"]');
    if (priceEl) {
      priceEl.textContent = currentVariant ? formatMoney(currentVariant.price, this.#moneyFormat) : '';
    }

    // Each partner's price follows its own size select.
    for (const row of this.querySelectorAll('.fandom-bundle__item')) {
      const select = row.querySelector('.fandom-bundle__variant');
      const target = row.querySelector('.fandom-bundle__price');
      const option = select?.selectedOptions?.[0];
      if (select && target && option) {
        target.textContent = formatMoney(Number(option.dataset.price || 0), this.#moneyFormat);
      }
    }

    const lines = this.#lines();
    const gross = lines.reduce((sum, l) => sum + l.price * l.quantity, 0);
    // The discount needs two qualifying items; with one, there is no saving to
    // promise and showing one would be a lie the cart then corrects.
    const qualifies = lines.length >= 2;
    const saving = qualifies ? Math.round((gross * this.#percent) / 100) : 0;

    const totalEl = this.querySelector('[ref="total"]');
    if (totalEl) totalEl.textContent = formatMoney(gross - saving, this.#moneyFormat);

    const saveEl = this.querySelector('[ref="save"]');
    if (saveEl) saveEl.textContent = `-${formatMoney(saving, this.#moneyFormat)}`;

    this.querySelector('[ref="saveRow"]')?.classList.toggle('hidden', !qualifies);
    this.classList.toggle('fandom-bundle--qualified', qualifies);

    const submit = this.querySelector('[ref="submit"]');
    if (submit) submit.disabled = lines.length === 0 || this.#busy;
  }

  #error(message) {
    const el = this.querySelector('[ref="error"]');
    if (!el) return;
    el.textContent = message || '';
    el.classList.toggle('hidden', !message);
  }

  async #submit() {
    if (this.#busy) return;
    const lines = this.#lines();
    if (!lines.length) return;

    this.#busy = true;
    this.#error('');
    const label = this.querySelector('[ref="submitLabel"]');
    const original = label?.textContent;
    if (label) label.textContent = 'Adding…';
    this.#render();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((l) => ({ merchandiseId: l.id, quantity: l.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines.map((l) => ({ id: Number(l.id), quantity: l.quantity })) }),
      });
      const body = await response.json();

      if (!response.ok || body.status) {
        this.#error(body.description || body.message || 'Could not add the bundle.');
        deferred.resolve({ cart: null, detail: { didError: true, source: 'fandom-bundle' } });
        return;
      }

      const cartResponse = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      // No `sections` in the detail on purpose — cart-items-component refetches
      // when they are absent, which is exactly the behaviour we want here.
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'fandom-bundle',
          itemCount: lines.length,
        },
      });
    } catch (error) {
      this.#error('Could not reach the cart. Please try again.');
      deferred.reject(error);
    } finally {
      this.#busy = false;
      if (label && original) label.textContent = original;
      this.#render();
    }
  }
}

if (!customElements.get('fandom-bundle')) {
  customElements.define('fandom-bundle', FandomBundle);
}
